// Shared month logic for Subscriptions and Expenses pages.
// Both modules track monthly obligations starting from March 2026.

export const MONTH_NAMES_RU = [
  "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
  "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь",
];

// Tracking starts in March 2026 — earlier months are never shown/counted.
export const TRACKING_START_YEAR = 2026;
export const TRACKING_START_MONTH = 2; // 0-indexed: March

export function ymKey(year, monthIdx) {
  return `${year}-${String(monthIdx + 1).padStart(2, "0")}`;
}

export function daysInMonth(year, monthIdx) {
  return new Date(year, monthIdx + 1, 0).getDate();
}

export function absMonth(year, monthIdx) {
  return year * 12 + monthIdx;
}

export const TRACKING_START_ABS = absMonth(TRACKING_START_YEAR, TRACKING_START_MONTH);

/**
 * Compute status for a record in a given month.
 * `record` must have: invoice_day (int 1-31) and payments ({YYYY-MM: bool, ...}).
 * Returns one of: "paid" | "waiting" | "soon" | "overdue" | "future"
 *
 * - paid: payments[month] === true
 * - future: month is later than current real month
 * - waiting: invoice day hasn't arrived yet in current month
 * - soon: invoice day passed but <= 5 days overdue (yellow)
 * - overdue: > 5 days overdue or any past month unpaid (red)
 */
export function computeStatus(record, year, monthIdx) {
  const month = ymKey(year, monthIdx);
  if (record.payments?.[month]) return "paid";

  const now = new Date();
  const curYear = now.getFullYear();
  const curMonth = now.getMonth();
  const curDay = now.getDate();

  if (year > curYear || (year === curYear && monthIdx > curMonth)) return "future";
  if (year < curYear || (year === curYear && monthIdx < curMonth)) return "overdue";

  const invoiceDay = Math.min(record.invoice_day, daysInMonth(year, monthIdx));
  if (curDay < invoiceDay) return "waiting";
  const diff = curDay - invoiceDay;
  if (diff <= 5) return "soon";
  return "overdue";
}

export const STATUS_META = {
  paid: { label: "Оплачено", bg: "bg-[#ebfaf2]", text: "text-[#00994C]", border: "border-[#00994C]" },
  waiting: { label: "Ждёт счёта", bg: "bg-white", text: "text-[#52525b]", border: "border-[#d4d4d8]" },
  soon: { label: "Срок подходит", bg: "bg-[#fff8e1]", text: "text-[#8a6d00]", border: "border-[#d9b400]" },
  overdue: { label: "Просрочено", bg: "bg-[#ffefe8]", text: "text-[#b3200b]", border: "border-[#FF2A00]" },
  future: { label: "Будущий период", bg: "bg-white", text: "text-[#52525b]", border: "border-[#d4d4d8]" },
};

/**
 * Sum unpaid amounts across all tracked months up to today (inclusive).
 * Includes "overdue" and "soon" statuses. Doesn't include "waiting" or "future".
 */
export function computeDueNow(records) {
  const now = new Date();
  const todayAbs = absMonth(now.getFullYear(), now.getMonth());
  let due = 0;
  for (const r of records) {
    for (let abs = TRACKING_START_ABS; abs <= todayAbs; abs += 1) {
      const y = Math.floor(abs / 12);
      const m = abs % 12;
      const st = computeStatus(r, y, m);
      if (st === "overdue" || st === "soon") {
        due += r.amount;
      }
    }
  }
  return due;
}
